import React from "react";
import { Button } from "primereact/button";
import {
  configProps,
  PossibleColumnProps,
  RowFromView,
  ViewNames,
} from "../types";
import { useSetDataByView } from "../hooks/useSetDataByView/useSetDataByView";
import { TransportOrdersForm } from "../types/transportOrders.ts";
import { confirmDialog } from "primereact/confirmdialog";

type StatusButtonsProps<
  TView extends Exclude<
    ViewNames,
    "drivers" | "companies" | "locations" | "products"
  >,
> = {
  rowData: RowFromView<TView>;
  columns: PossibleColumnProps<RowFromView<TView>>[];
  fieldNameMap?: configProps<TView>["fieldNameMap"];
  viewName: TView;
};

const fieldsNotReadyForWaybillerErrorTranslations: Record<string, string> = {
  string_too_short: "Zbyt krótki",
  string_too_long: "Zbyt długo",
};

export default function StatusButtons<
  TView extends Exclude<
    ViewNames,
    "drivers" | "companies" | "locations" | "products"
  >,
>({
  rowData,
  columns,
  fieldNameMap = {},
  viewName,
}: StatusButtonsProps<TView>) {
  const row = rowData;
  const currentStatus = row?.entryStatus ?? "default";
  const hasDeliveryStatus =
    "deliveryStatus" in row && row.deliveryStatus !== undefined;
  const contractPage =
    viewName === "contracts" || viewName === "goodsToTakeAway";

  // Map backend field to column names if possible
  function translateField(
    field: string,
    columns: { field: string; header: string }[],
    fieldNameMap: NonNullable<configProps<TView>["fieldNameMap"]>,
  ) {
    const mappedField = fieldNameMap[field] ?? field;
    return (
      columns.find((col) => col.field === mappedField)?.header ?? "Inne pole"
    );
  }

  const notReadyForWaybiller =
    "fieldsNotReadyForWaybiller" in row
      ? Object.entries(row.fieldsNotReadyForWaybiller)
          // Filter out fields that are not present in the columns
          .filter(([field, _errors]) => {
            const mappedField = fieldNameMap[field] ?? field;
            return columns.some((col) => col.field === mappedField);
          })
          .map(([field, errors]) =>
            errors
              .map(
                (err) =>
                  `${translateField(field, columns, fieldNameMap)}: ${fieldsNotReadyForWaybillerErrorTranslations[err] ?? "Nieprawidłowa wartość"}`,
              )
              .join("\n"),
          )
          .join("\n")
      : undefined;

  const { setData } = useSetDataByView<TView>();

  const toggleStatus = React.useCallback(
    (status: TransportOrdersForm["entryStatus"]) => {
      const oldStatus = rowData.entryStatus ?? "default";
      const newStatus = rowData.entryStatus === status ? "default" : status;

      if (oldStatus !== newStatus) {
        void setData([
          {
            id: rowData.id,
            // NOTE: this is a hack, I would put this in the arg typing, but we also use toggleStatus("cancelled"), which is not a valid TransportOrdersForm["entryStatus"] :/
            entryStatus: newStatus,
          },
        ]);
      }
    },
    [setData, rowData],
  );

  const confirmDelete = React.useCallback(() => {
    confirmDialog({
      message: `Czy na pewno chcesz usunąć ten wpis?`,
      header: "Potwierdź usunięcie",
      icon: "pi pi-exclamation-triangle",
      acceptLabel: "Usuwać",
      rejectLabel: "Anulować",
      accept: () => toggleStatus("deleted"),
      acceptClassName: "p-button-danger",
      className: "custom-confirm-dialog",
    });
  }, [toggleStatus]);

  const disableCompleted =
    "readyForWaybiller" in row &&
    row.readyForWaybiller === false &&
    currentStatus !== "completed";
  const disablePending =
    "readyForWaybiller" in row &&
    row.readyForWaybiller === false &&
    currentStatus !== "pending";

  return (
    <div className={"status-buttons-container"}>
      <Button
        severity="success"
        size="small"
        icon="pi pi-check"
        outlined={currentStatus !== "completed"}
        onClick={() => toggleStatus("completed")}
        title="Zakończony"
        disabled={disableCompleted}
      />
      {!contractPage && (
        <Button
          severity="secondary"
          size="small"
          icon="pi pi-hourglass"
          outlined={currentStatus !== "pending"}
          onClick={() => toggleStatus("pending")}
          title="Spodziewany"
          disabled={disablePending}
          tooltip={notReadyForWaybiller}
          tooltipOptions={{
            position: "left",
            showOnDisabled: true,
            autoHide: false,
            className: "entry-status__tooltip-content",
          }}
        />
      )}
      {/* not Transport Order or Forwarding Order */}
      {!hasDeliveryStatus && (
        <Button
          severity="danger"
          size="small"
          icon="pi pi-trash"
          outlined={currentStatus !== "deleted"}
          onClick={confirmDelete}
          title="Usuwać"
        />
      )}
    </div>
  );
}
